import { SortDropdown } from '@rocco/ui/filter-sort/components/SortDropdown';
import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { FiltersBar } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBar';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { FiltersModal } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { SortOption } from '@rocco/types/enum/sort-option';
import { ChevronRightIcon } from 'lucide-react';
import { Button } from '@rocco/components/button';

interface FiltersContextType {
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    displayMode: DisplayMode;
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

export const useFiltersContext = () => {
    const context = useContext(FiltersContext);

    if (!context) {
        throw new Error(
            'useFiltersContext must be used within a FilterDropdownProvider',
        );
    }

    return context;
};

interface FiltersProps extends React.PropsWithChildren {
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    displayMode: DisplayMode;
    stickyComponent?: React.ReactNode;
    tabContainerRef?: React.RefObject<HTMLDivElement>;
    secondaryComponent?: React.ReactNode;
    showSortBy?: boolean;
}

export interface FilterSortOption {
    title: string;
    value: SortOption;
    selected: boolean;
}

export const Filters = (props: FiltersProps) => {
    const {
        children,
        searchFilters,
        sortOptions,
        displayMode,
        stickyComponent,
        tabContainerRef,
        secondaryComponent,
        showSortBy,
    } = props;

    const [isOverflowing, setIsOverflowing] = useState(false);
    const scrollableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkOverflow = () => {
            if (scrollableRef.current) {
                setIsOverflowing(
                    scrollableRef.current.scrollWidth >
                        scrollableRef.current.clientWidth,
                );
            }
        };

        checkOverflow(); // Initial check

        window.addEventListener('resize', checkOverflow); // Re-check on window resize

        return () => {
            window.removeEventListener('resize', checkOverflow); // Cleanup on unmount
        };
    }, [scrollableRef]);

    const isDesktop = displayMode === DisplayMode.Desktop;

    const handleScrollToEnd = () => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo({
                left: scrollableRef.current.scrollWidth,
                behavior: 'smooth',
            });
        }
    };

    return (
        <FiltersContext.Provider
            value={{ searchFilters, sortOptions, displayMode }}
        >
            <div
                ref={tabContainerRef}
                className="grid items-center w-full gap-2"
                style={{
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                }}
            >
                <div className="flex items-center">
                    {searchFilters.config.modalFilters.length > 0 && (
                        <FiltersModal showSorting={!showSortBy} />
                    )}
                </div>

                <div className="flex relative min-w-0">
                    <div
                        ref={scrollableRef}
                        className="scrollbar-hide flex items-center gap-2 overflow-x-auto"
                        style={
                            isOverflowing
                                ? {
                                      maskImage:
                                          'linear-gradient(to right, black, black 90%, transparent)',
                                      WebkitMaskImage:
                                          'linear-gradient(to right, black, black 90%, transparent)',
                                      paddingRight: '4rem',
                                  }
                                : undefined
                        }
                    >
                        <div className="flex items-center gap-2 flex-nowrap">
                            {children}
                        </div>
                        {isDesktop && <FiltersBar />}
                    </div>
                    {isOverflowing && (
                        <Button
                            color={'transparent'}
                            onClick={handleScrollToEnd}
                            size={'square36'}
                            layoutMode={'noShrink'}
                        >
                            <ChevronRightIcon className="size-4.5 text-gray-500" />
                        </Button>
                    )}
                </div>

                {(stickyComponent ||
                    secondaryComponent ||
                    (showSortBy && isDesktop)) && (
                    <div className="flex items-center gap-2">
                        {!isDesktop &&
                            searchFilters.config.modalFilters.length > 0 && (
                                <FiltersModal showSorting={!showSortBy} />
                            )}
                        {stickyComponent}
                        {secondaryComponent}
                        {showSortBy && isDesktop && (
                            <SortDropdown sortOptions={sortOptions} />
                        )}
                    </div>
                )}
            </div>
        </FiltersContext.Provider>
    );
};
